import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Bot, Loader2, ArrowLeft, Eye, EyeOff, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail, base_url: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset email");
    } finally {
      setForgotLoading(false);
    }
  };
  const openForgot = () => { setForgotEmail(email || ""); setForgotSent(false); setForgotOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0F17] text-slate-200">
      {/* left ambient panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative p-12 overflow-hidden border-r border-slate-800/70">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] bg-emerald-500/15 rounded-full blur-[120px]" />
        <Link to="/" className="relative z-10 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-[#0B0F17]" />
          </div>
          <span className="font-display font-extrabold text-xl text-white">
            DVEPL
          </span>
        </Link>
        <div className="relative z-10">
          <h2 className="font-display text-4xl font-extrabold leading-tight text-white">
            Your AI-powered<br />WhatsApp command center.
          </h2>
          <p className="mt-5 text-slate-400 max-w-md leading-relaxed">
            Auto-reply rules, a Gemini knowledge base, broadcast campaigns and a realistic simulator — everything in one place.
          </p>
        </div>
        <div className="relative z-10 text-xs font-mono text-slate-600 uppercase tracking-wider">
          Demo mode · No real messages sent
        </div>
      </div>

      {/* form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-sm text-slate-400 mb-8">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <h1 className="font-display text-3xl font-bold text-white">
            {isLogin ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-2 text-slate-400 text-sm">
            {isLogin ? "Log in to your automation dashboard." : "Start automating WhatsApp in minutes."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-slate-300">Full name</Label>
                <Input id="name" data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  required placeholder="Jane Doe"
                  className="mt-1.5 h-11 bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" data-testid="auth-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com"
                className="mt-1.5 h-11 bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" data-testid="auth-password-input" type={showPw ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••"
                  className="h-11 pr-11 bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500" />
                <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-300">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {isLogin && (
                <div className="mt-2 text-right">
                  <button type="button" data-testid="forgot-password-link" onClick={openForgot}
                    className="text-xs text-emerald-400 hover:underline">
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            {error && (
              <p data-testid="auth-error" className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <Button type="submit" data-testid="auth-form-submit-button" disabled={loading}
              className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isLogin ? "Log in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-400 text-center">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <Link to={isLogin ? "/signup" : "/login"} data-testid="auth-switch-link"
              className="text-emerald-400 font-medium hover:underline">
              {isLogin ? "Sign up" : "Log in"}
            </Link>
          </p>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) setForgotSent(false); }}>
        <DialogContent data-testid="forgot-password-dialog" className="bg-[#0d1420] border-slate-800 text-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Reset your password</DialogTitle>
            <DialogDescription className="text-slate-400">
              {forgotSent
                ? "If that email is registered, a reset link is on its way."
                : "Enter your account email and we'll send you a secure link to set a new password."}
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 flex flex-col items-center text-center gap-3" data-testid="forgot-password-sent">
              <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center">
                <MailCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="text-sm text-slate-300">Check your inbox for a password reset link. It expires in 1 hour.</p>
              <Button data-testid="forgot-done-btn" onClick={() => setForgotOpen(false)}
                className="mt-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">Done</Button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4">
              <div>
                <Label className="text-slate-300 text-xs">Email</Label>
                <Input data-testid="forgot-email-input" type="email" value={forgotEmail} required
                  onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@company.com"
                  className="mt-1.5 h-11 bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500" />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="forgot-submit-btn" disabled={forgotLoading}
                  className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-[#0B0F17] font-semibold">
                  {forgotLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
